<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import MessageFormatter from 'shared/helpers/MessageFormatter';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import AudioChip from 'dashboard/components-next/message/chips/Audio.vue';

const props = defineProps({
  message: { type: Object, required: true },
});

const { t } = useI18n();
const formattedContent = computed(() => {
  if (props.message.content_attributes?.content_format !== 'markdown')
    return '';
  const formatter = new MessageFormatter(props.message.content);
  formatter.disableImageRendering();
  return formatter.formattedMessage;
});
const audioAttachment = attachment => ({
  dataUrl: new URL(attachment.file_url, window.location.origin).href,
  fileType: attachment.file_type,
  extension: attachment.extension,
});
</script>

<template>
  <p v-if="message.deleted" class="mb-0 italic opacity-75">
    {{ t('INTERNAL_CHAT.MESSAGE_DELETED') }}
  </p>
  <slot v-else-if="$slots.editor" name="editor" />
  <div
    v-else-if="formattedContent"
    v-dompurify-html="formattedContent"
    class="prose prose-bubble max-w-full break-words text-inherit dark:prose-invert prose-p:my-0 prose-pre:bg-n-alpha-2 prose-pre:text-n-slate-12"
  />
  <p v-else-if="message.content" class="mb-0 whitespace-pre-wrap break-words">
    {{ message.content }}
  </p>
  <div
    v-if="!message.deleted && message.attachments?.length"
    class="mt-2 grid grid-cols-1 min-w-0 gap-1"
  >
    <template v-for="attachment in message.attachments" :key="attachment.id">
      <div
        v-if="attachment.file_type === 'audio'"
        class="w-80 max-w-full min-w-0 text-n-slate-12"
      >
        <AudioChip
          compact
          :attachment="audioAttachment(attachment)"
          :show-transcribed-text="false"
          data-testid="internal-chat-audio-player"
        />
      </div>
      <a
        v-else
        class="flex items-center gap-2 text-xs underline"
        :href="attachment.file_url"
        target="_blank"
        rel="noopener noreferrer"
      >
        <Icon icon="i-lucide-paperclip" class="size-3 shrink-0" />
        <span class="min-w-0 break-all">{{ attachment.filename }}</span>
      </a>
    </template>
  </div>
</template>
